<template>
  <div
    class="relative max-h-[80vh] min-h-78 space-y-9"
    data-cy="stips-container"
  >
    <!-- Header -->
    <div v-if="!viewOnly" class="flex w-full flex-col space-y-5">
      <div
        class="sticky top-0 z-1 flex bg-white pb-4"
        :class="hideTitle ? 'justify-end' : 'justify-between'"
      >
        <lf-h1 v-if="!hideTitle">
          {{ $t("DEALS.DEAL_PROGRESS.EDIT_REQ_STIPS") }}
        </lf-h1>
        <div class="flex shrink-0 justify-end space-x-2 lg:col-span-2">
          <outline-button
            :disabled="!selectedStipsIds.length || viewOnly"
            data-cy="unsellect-all-stips"
            @click="selectedStipsIds = []"
          >
            {{ `${$t("COMMON.UNSELECT")}  ${$t("COMMON.ALL")}` }}
          </outline-button>
          <lf-button
            v-if="canAddStips && !viewOnly"
            :disabled="isLoading"
            class="bg-primary/10 font-semibold text-primary"
            data-cy="createNewStipButton"
            @click="onNewStipClick"
          >
            {{ $t("STIPS.NEW_STIP") }}
          </lf-button>
        </div>
      </div>
      <search-input
        class="h-11"
        no-margin
        no-padding
        :model-value="search"
        @update:model-value="updateSearch"
      />
    </div>

    <!-- Stips -->
    <loader :is-loading="isLoading" />
    <div
      ref="stipsContainer"
      class="stips-list-wrapper min-h-78 overflow-y-auto"
      data-cy="stip-list"
    >
      <stip
        v-for="(stip, index) in stipsList"
        v-model="selectedStipsIds"
        :key="stip.id || index"
        class="max-h-16"
        :stip="{
          ...stip,
          description: stipDescriptions[stip.id] || '',
          count: stipCounts[stip.id] || 1,
        }"
        :disabled="viewOnly"
        :select-only="selectOnly"
        @update:stip="updateStip"
      />
      <div class="relative">
        <form
          v-if="isCreatingStip"
          class="absolute max-h-58 w-full rounded-sm border border-divider bg-white p-3"
          @submit="onSubmit"
        >
          <div class="mb-3 flex w-full flex-1 justify-between px-1">
            <button
              class="cursor-pointer font-medium text-gray-400 focus:outline-hidden"
              @click="isCreatingStip = false"
            >
              {{ $t("COMMON.CANCEL") }}
            </button>
            <button
              type="submit"
              class="cursor-pointer font-semibold text-primary focus:outline-hidden"
            >
              {{ $t("COMMON.SAVE") }}
            </button>
          </div>
          <lf-input
            :placeholder="$t('STIPS.STIP_NAME')"
            :value="newStip.name"
            name="name"
            type="text"
            class="text-xs"
            no-margin
            ref="newStipNameRef"
          />
          <lf-input
            :placeholder="$t('STIPS.ADD_DESCRIPTION')"
            name="description"
            :value="newStip.description"
            no-margin
            class="mt-1-5"
            :maxlength="235"
          />
          <stip-dropdown-categories
            name="file_type"
            class="mt-3"
            :placeholder="$t('STIPS.SELECT_FILE_TYPE')"
            dropdown-teleported
            v-model="selectedDocumentType"
          />
        </form>
        <button
          v-else-if="canAddStips && !selectOnly && !viewOnly"
          class="flex h-13 w-full items-center justify-center space-x-3 rounded-sm border border-divider p-3 hover:bg-gray-50 focus:outline-hidden"
          @click="onNewStipClick"
        >
          <span class="font-semibold">
            {{ $t("DEALS.DEAL_PROGRESS.ADD_NEW_STIP") }}
          </span>
          <icon-base :icon="IconPlusSmall" class="text-secondary" />
        </button>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { useInfiniteScroll } from "@vueuse/core";
import { computed, nextTick, reactive, ref, watch } from "vue";
import { useStore } from "vuex";

import uniq from "lodash/uniq";
import { useField, useForm } from "vee-validate";

import { useAuth } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";

import StipDropdownCategories from "@/components/deals/StipDropdownCategories.vue";
import IconPlusSmall from "@/components/icons/IconPlusSmall.vue";
import IconBase from "@/components/ui/IconBase.vue";
import Loader from "@/components/ui/Loader.vue";
import LfButton from "@/components/ui/buttons/LfButton.vue";
import OutlineButton from "@/components/ui/buttons/OutlineButton.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";
import Stip from "@/views/deals/components/Stip.vue";

import type { IRequiredStipForOffer, IStip } from "@/models/applications";
import type { IStipsState } from "@/models/stips";
import type { PropType } from "vue";

type StipDescriptions = Record<number, IRequiredStipForOffer["description"]>;
type StipCounts = Record<number, IRequiredStipForOffer["count"]>;
type StipFileTypes = Record<number, IRequiredStipForOffer["file_type"]>;

const props = defineProps({
  hideTitle: {
    type: Boolean,
    default: false,
  },
  requiredStips: {
    type: Array as PropType<Array<IRequiredStipForOffer> | Array<IStip>>,
    default: () => [],
  },
  preselectStips: {
    type: Array as PropType<number[] | string[]>,
    default: () => [],
  },
  requiredStipsFirst: {
    type: Boolean,
  },
  viewOnly: {
    type: Boolean,
  },
  selectOnly: {
    type: Boolean,
    default: false,
  },
});

const selectedDocumentType = ref();
const { getters, dispatch } = useStore();
const { isAdmin, isClientAdmin, isClientUnderwriter } = useAuth();

const stipsContainer = ref<HTMLDivElement | null>(null);
const stipsPage = ref(1);
const search = ref("");
const newStipNameRef = ref<InstanceType<typeof LfInput> | null>(null);
const newStip = reactive({
  name: "",
  description: "",
});
const isCreatingStip = ref(false);

const isLoading = computed<boolean>(() => getters["stips/isLoading"]);
const stipsData = computed<IStipsState>(() => getters["stips/stipsData"]);

const searchResults = ref<IStip[]>();

const stipsList = computed<IStip[]>(() => {
  return search.value.length ? searchResults.value || [] : stipsData.value.data;
});

const getStipId = (stip: IRequiredStipForOffer | IStip) =>
  stip.stip_id ?? stip.id;

const selectedStipsIds = ref(
  props.requiredStips.map((stip) => getStipId(stip))
);

const stipDescriptions = reactive<StipDescriptions>({});

const stipCounts = reactive<StipCounts>({});
props.requiredStips.forEach(
  (stip) => (stipCounts[getStipId(stip)] = stip.count)
);

const stipFileTypes = computed<StipFileTypes>(() => {
  const result: StipFileTypes = {};
  props.requiredStips.forEach(
    (stip) => (result[getStipId(stip)] = stip.file_type)
  );
  return result;
});

const canAddStips = computed(
  () => isAdmin || isClientAdmin || isClientUnderwriter
);

const { handleChange, value: selectedStips } = useField("stips", undefined, {
  initialValue: props.requiredStips.map((stip) => {
    return {
      id: getStipId(stip),
      description: stip.description ?? null,
      file_type: stip.file_type ?? null,
      count: stip.count ?? 1,
    };
  }),
});

const updateStip = (updatedStip: IStip) => {
  const stipId = updatedStip.id;

  stipDescriptions[stipId] = updatedStip.description;
  stipCounts[stipId] = updatedStip.count;
};

const updateStips = () => {
  handleChange(
    selectedStipsIds.value.map((id) => {
      return {
        id,
        description: stipDescriptions[id] || null,
        file_type: stipFileTypes.value[id] ?? null,
        count: stipCounts[id] ?? 1,
      };
    })
  );
};

const { handleSubmit } = useForm({
  initialValues: newStip,
});

const onSubmit = handleSubmit(async (values) => {
  const newStipId: number = await dispatch("stips/createStip", {
    name: values.name,
    description: values.description,
    file_type: selectedDocumentType.value,
  }).then((res) => res.id);

  selectedStipsIds.value.push(newStipId);
  stipDescriptions[newStipId] = values.description ?? null;
  isCreatingStip.value = false;
});

const externalOnSubmit = async () => {
  if (isCreatingStip.value) {
    await onSubmit();
  }
  return selectedStips.value;
};

const onNewStipClick = () => {
  isCreatingStip.value = true;
  nextTick(() => {
    newStipNameRef.value?.$el?.querySelector("input")?.focus();
  });
};

const { fetchWrapper: getStipsList } = usePromiseWrapper(
  async (append: boolean = true) => {
    const results = await dispatch("stips/getStips", {
      append,
      params: { page: stipsPage.value, search: search.value },
    });
    stipsPage.value++;
    return results;
  }
);

const updateSearch = async (value: string) => {
  search.value = value;
  stipsPage.value = 1;
  searchResults.value = await getStipsList();
};

useInfiniteScroll(stipsContainer, async () => await getStipsList(), {
  distance: 100,
  canLoadMore: () => {
    const { meta } = stipsData.value;
    return !isLoading.value && meta.current_page < meta.last_page;
  },
});

const setupPreselectedStips = async () => {
  if (
    props.preselectStips.length ||
    (props.requiredStipsFirst && props.requiredStips.length)
  ) {
    const requiredStipIdsFromTemplate: IStip[] = await dispatch(
      "stips/getStips",
      {
        params: {
          ids: props.preselectStips,
        },
        append: !props.viewOnly,
      }
    );
    if (props.preselectStips.length) {
      selectedStipsIds.value = uniq([
        ...selectedStipsIds.value,
        ...requiredStipIdsFromTemplate.map((stip) => stip.id),
      ]);
    }
  }
  getStipsList();
};

watch(
  [selectedStipsIds, stipDescriptions, stipFileTypes, stipCounts],
  updateStips,
  {
    immediate: true,
  }
);

watch(() => props.preselectStips.length, setupPreselectedStips, {
  immediate: true,
});

watch(
  () => props.requiredStips,
  () => {
    for (const key in stipDescriptions) {
      delete stipDescriptions[key];
    }

    for (const key in stipCounts) {
      delete stipCounts[key];
    }
    props.requiredStips.forEach((stip) => {
      stipDescriptions[getStipId(stip)] = stip.description;
      stipCounts[getStipId(stip)] = stip.count;
    });
  },
  { immediate: true }
);

defineExpose({ externalOnSubmit });
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.stips-list-wrapper {
  @apply relative grid grid-cols-1 justify-around gap-3 md:grid-cols-2 lg:grid-cols-3;
  max-height: calc(70vh - 200px);
}
</style>
